import { useState } from 'react'
import { UNITS, asinOf, gtinOf } from '@domain/product'
import type { Product, Unit } from '@domain/product'
import type { ProductPatch } from '@domain/ports/finance-repository'
import type { Category } from '@domain/finance'
import { Botao } from '@ui/glass/Botao'
import { Glass } from '@ui/glass/Glass'
import { FolhaDeBaixo } from '@ui/shell/FolhaDeBaixo'
import { SeletorDeCategoria } from '@ui/finance/SeletorDeCategoria'
import { useFinanceRepositories } from '@ui/finance/finance-context'
import { Scanner } from './Scanner'
import { EscolherFoto } from './EscolherFoto'
import { baixarImagem, preparePhoto } from './prepare-photo'
import type { FotoPreparada } from './prepare-photo'

/** O cabeçalho de uma seção do formulário. Largura cheia dentro da grade. */
function Secao({ children }: { children: React.ReactNode }) {
  return (
    <p className="mt-2 flex items-center gap-2 text-[10px] font-semibold tracking-[0.16em] text-[var(--text-faint)] uppercase sm:col-span-2">
      {children}
      <span aria-hidden="true" className="h-px flex-1 bg-[var(--border)]" />
    </p>
  )
}

/**
 * Uma limitação dita ao lado do campo que a produz.
 *
 * Todas moravam num parágrafo no fim do formulário — que é onde um aviso vai
 * para não ser lido, porque quem chegou até lá já decidiu salvar.
 */
function Aviso({ children }: { children: React.ReactNode }) {
  return <p className="text-xs text-[var(--text-faint)] sm:col-span-2">{children}</p>
}

/** Os campos que a busca pelo código consegue preencher — cinco dos nove. */
type CampoDaBase = 'name' | 'brand' | 'size' | 'imageUrl'

/**
 * O que a base pública respondeu.
 *
 * Era uma `string` solta, e as três respostas possíveis são diferentes demais
 * para caberem numa: *achei* precisa dizer **o quê** e **quanto** preencheu,
 * *não achei* é resposta final, e *falhei ao perguntar* é a única que melhora
 * tentando de novo — e por isso a única que ganha um botão.
 */
type Achado =
  | { tipo: 'achou'; nome: string | null; quantos: number }
  | { tipo: 'nada' }
  | { tipo: 'falhou' }

/**
 * A marca de "isto veio da base, confira".
 *
 * Um campo preenchido por uma base colaborativa e um campo digitado por quem
 * tem a embalagem na mão têm o mesmo aspecto — e valem coisas diferentes. O
 * princípio 5 desta casa é que o app sugere e a pessoa decide; sem dizer qual
 * é qual, ela decidiria sem saber sobre o que está decidindo.
 */
function DaBase() {
  return (
    <span className="text-[10px] text-[var(--economia)]" title="Confira antes de salvar">
      · da base
    </span>
  )
}

/** O rótulo de um campo, que diz quando o valor abaixo dele veio de fora. */
function Rotulo({ children, daBase = false }: { children: React.ReactNode; daBase?: boolean }) {
  return (
    <span className="flex items-baseline gap-1 text-xs text-[var(--text-muted)]">
      {children}
      {daBase && <DaBase />}
    </span>
  )
}

type Props = {
  /** O produto a editar. `null` abre em branco, para criar um. */
  produto: Product | null
  categorias: Category[]
  onFechar: () => void
  /** Depois de gravar. Quem chamou decide o que reler. */
  onGravado: (id: string) => void
  /** Uma categoria nova foi criada daqui — a lista de cima que releia. */
  onCategoriasMudaram: () => void
}

/**
 * A folha que cadastra E edita um produto.
 *
 * ## Eram dois formulários para os mesmos nove campos
 *
 * Cadastrar era esta folha de baixo, com foto, scanner e busca pelo código.
 * Editar era um link sublinhado dentro da dobra "Ficha do produto", que abria
 * uma grade solta **sem nada disso** — nem foto, nem câmera, nem busca. Editar
 * era uma versão pior de cadastrar, e ninguém decidiu isso: as duas telas
 * foram escritas em semanas diferentes. Agora é uma só, e o que muda entre os
 * modos é o título, o verbo do botão e o que a porta faz.
 *
 * ## A porta some quando o produto JÁ tem código
 *
 * O alvo de escanear existe para **identificar uma embalagem desconhecida**
 * (decisão 238). Editando um produto que já tem código de barras, não há nada
 * a identificar, e o painel viraria enfeite ocupando o topo. Editando um que
 * **não** tem, ele continua sendo o caminho certo — e é aí que a regra fica
 * melhor do que um `if (criando)`: o que decide é o código faltar, não o modo.
 *
 * ## Só o que MUDOU é gravado
 *
 * No modo de edição o `update` recebe um patch com os campos alterados, e não
 * o produto inteiro. Mandar tudo faria toda edição reescrever `updatedAt` de
 * campos intocados e disputar com quem editasse ao mesmo tempo em outro
 * celular — dois deles, nesta casa.
 */
export function FolhaDeProduto({
  produto,
  categorias,
  onFechar,
  onGravado,
  onCategoriasMudaram,
}: Props) {
  const { productRepository, productLookup } = useFinanceRepositories()
  const criando = produto === null

  const [nome, setNome] = useState(produto?.name ?? '')
  const [marca, setMarca] = useState(produto?.brand ?? '')
  const [categoria, setCategoria] = useState<string | null>(produto?.categoryId ?? null)
  const [tamanho, setTamanho] = useState(produto?.size == null ? '' : String(produto.size))
  const [unidade, setUnidade] = useState<Unit>(produto?.unit ?? 'ml')
  const [pacote, setPacote] = useState(String(produto?.packQuantity ?? 1))
  const [codigo, setCodigo] = useState(produto?.barcode ?? '')
  const [asin, setAsin] = useState(produto?.asin ?? '')

  const [foto, setFoto] = useState<FotoPreparada | null>(null)
  const [fotoDaBase, setFotoDaBase] = useState<string | null>(null)
  /** A pessoa tirou a foto que já estava salva. Só se cumpre no salvar. */
  const [removeuFoto, setRemoveuFoto] = useState(false)

  const [escaneando, setEscaneando] = useState(false)
  const [buscando, setBuscando] = useState(false)
  const [achado, setAchado] = useState<Achado | null>(null)
  const [jaBuscado, setJaBuscado] = useState<string | null>(null)
  const [daBase, setDaBase] = useState<ReadonlySet<CampoDaBase>>(new Set())
  /**
   * A porta não aparece. Já nasce dispensada quando o produto tem código: não
   * há embalagem a identificar. Ver o cabeçalho.
   */
  const [semCodigo, setSemCodigo] = useState(produto?.barcode != null)
  const [salvando, setSalvando] = useState(false)
  const [erro, setErro] = useState<string | null>(null)

  /**
   * Preenche o formulário com o que a base pública sabe.
   *
   * **Só preenche o que está VAZIO.** Sobrescrever o que a pessoa já digitou
   * seria a busca discordando de quem tem a embalagem na mão — e ela está mais
   * certa que uma base colaborativa. Princípio 5: é sugestão, e quem salva é ela.
   */
  const buscarPeloCodigo = async (cru: string = codigo) => {
    const gtin = gtinOf(cru)
    if (gtin === null) return
    setBuscando(true)
    setAchado(null)
    setJaBuscado(gtin)
    try {
      const sugestao = await productLookup.byGtin(gtin)
      if (sugestao === null) {
        setAchado({ tipo: 'nada' })
        return
      }
      // O que a base preencheu é contado aqui, e não estimado depois: só este
      // ponto sabe a diferença entre "veio vazio da base" e "a pessoa já tinha
      // digitado", e as duas terminam com o campo cheio.
      const vindos = new Set<CampoDaBase>()
      if (sugestao.name !== null && nome.trim() === '') {
        setNome(sugestao.name)
        vindos.add('name')
      }
      if (sugestao.brand !== null && marca.trim() === '') {
        setMarca(sugestao.brand)
        vindos.add('brand')
      }
      if (sugestao.size !== null && tamanho.trim() === '') {
        setTamanho(String(sugestao.size))
        vindos.add('size')
      }
      if (sugestao.unit !== null && tamanho.trim() === '') setUnidade(sugestao.unit)
      // Só interessa se não há foto: a do produto ganha da base sempre.
      if (produto?.imageUrl == null) {
        setFotoDaBase(sugestao.imageUrl)
        if (sugestao.imageUrl !== null) vindos.add('imageUrl')
      }
      setDaBase(vindos)
      setAchado({ tipo: 'achou', nome: sugestao.name, quantos: vindos.size })
    } catch (erroDaBusca: unknown) {
      // Não achar e não conseguir perguntar são coisas diferentes: só a segunda
      // melhora tentando de novo, e só ela ganha o botão de repetir.
      console.error('FolhaDeProduto: a busca pelo código falhou', erroDaBusca)
      setAchado({ tipo: 'falhou' })
    } finally {
      setBuscando(false)
    }
  }

  /**
   * A pessoa editou um campo que veio da base — logo ele não veio mais dela.
   *
   * A marca diz "confira isto, é palpite de estranho". Depois que alguém
   * digitou por cima, o palpite acabou, e manter a marca seria o rótulo
   * mentindo sobre a origem do que está na tela.
   */
  const esqueceAOrigem = (campo: CampoDaBase) => {
    setDaBase((atuais) => {
      if (!atuais.has(campo)) return atuais
      const proximo = new Set(atuais)
      proximo.delete(campo)
      return proximo
    })
  }

  /**
   * Digitar ou colar um código VÁLIDO já dispara a busca.
   *
   * Um GTIN só é válido com o dígito verificador fechando, então não há como
   * isto disparar no meio da digitação: ou o código está completo, ou `gtinOf`
   * devolve `null`. O `jaBuscado` impede a consulta de repetir enquanto o
   * resto do formulário é preenchido — o código continua válido o tempo todo.
   */
  const mudarCodigo = (valor: string) => {
    setCodigo(valor)
    const gtin = gtinOf(valor)
    if (gtin !== null && gtin !== jaBuscado) void buscarPeloCodigo(valor)
  }

  /**
   * Aplica a foto **depois** de o produto existir, e sem travar o salvamento.
   *
   * O erro de subir é dito; o de importar da base, não. Quem tirou uma foto de
   * propósito precisa saber que ela não entrou; a importação automática pode
   * falhar em silêncio porque ninguém pediu por ela.
   */
  const aplicarFoto = (id: string) => {
    if (foto !== null) {
      void productRepository
        .setImage(id, foto.blob)
        .then(() => {
          onGravado(id)
        })
        .catch((e: unknown) => {
          console.error('FolhaDeProduto: a foto não subiu', e)
          setErro('salvou, mas a foto não subiu — dá para tentar de novo')
        })
      return
    }
    if (removeuFoto) {
      void productRepository
        .clearImage(id)
        .then(() => {
          onGravado(id)
        })
        .catch((e: unknown) => {
          console.error('FolhaDeProduto: a foto não saiu', e)
          setErro('salvou, mas a foto não foi removida')
        })
      return
    }
    // A da base é a última opção, e só quando ninguém escolheu nada: quem tirou
    // a foto tem a embalagem na mão, e a da base é o palpite de um estranho.
    if (fotoDaBase !== null) {
      void baixarImagem(fotoDaBase)
        .then(async (baixada) => {
          if (baixada === null) return
          const preparada = await preparePhoto(baixada)
          await productRepository.setImage(id, preparada.blob)
          onGravado(id)
        })
        .catch((e: unknown) => {
          console.error('FolhaDeProduto: não deu para importar a foto da base', e)
        })
    }
  }

  const salvar = () => {
    if (nome.trim() === '') {
      setErro('escreva o nome do produto')
      return
    }
    const size = tamanho.trim() === '' ? null : Number(tamanho.replace(',', '.'))
    if (size !== null && (!Number.isFinite(size) || size <= 0)) {
      setErro('tamanho inválido')
      return
    }
    const packQuantity = Number(pacote)
    if (!Number.isInteger(packQuantity) || packQuantity < 1) {
      setErro('quantidade do pacote inválida')
      return
    }
    setErro(null)
    setSalvando(true)

    const campos = {
      name: nome.trim(),
      brand: marca.trim() === '' ? null : marca.trim(),
      categoryId: categoria,
      size,
      unit: size === null ? null : unidade,
      packQuantity,
      barcode: codigo.trim() === '' ? null : codigo.trim(),
      asin: asin.trim() === '' ? null : asin.trim(),
    }

    if (produto === null) {
      void productRepository
        .create({
          ...campos,
          isMonitored: false,
          // A família é escolhida no detalhe do produto, onde a comparação por
          // unidade base está à vista. Aqui seria um campo sem contexto nenhum.
          familyId: null,
        })
        .then((id) => {
          aplicarFoto(id)
          onGravado(id)
          onFechar()
        })
        .catch(() => {
          setErro('não foi possível salvar agora')
        })
        .finally(() => {
          setSalvando(false)
        })
      return
    }

    // Só o que MUDOU. Ver o cabeçalho.
    const patch: ProductPatch = {}
    const poe = <K extends keyof ProductPatch>(
      campo: K,
      valor: ProductPatch[K],
      atual: unknown,
    ) => {
      if (valor !== atual) patch[campo] = valor
    }
    poe('name', campos.name, produto.name)
    poe('brand', campos.brand, produto.brand)
    poe('categoryId', campos.categoryId, produto.categoryId)
    poe('size', campos.size, produto.size)
    poe('unit', campos.unit, produto.unit)
    poe('packQuantity', campos.packQuantity, produto.packQuantity)
    poe('barcode', campos.barcode, produto.barcode)
    poe('asin', campos.asin, produto.asin)

    const gravaCampos =
      Object.keys(patch).length === 0
        ? Promise.resolve()
        : productRepository.update(produto.id, patch)

    void gravaCampos
      .then(() => {
        aplicarFoto(produto.id)
        onGravado(produto.id)
        onFechar()
      })
      .catch((e: unknown) => {
        console.error('FolhaDeProduto: não deu para salvar', e)
        setErro('não foi possível salvar agora')
      })
      .finally(() => {
        setSalvando(false)
      })
  }

  return (
    <FolhaDeBaixo titulo={criando ? 'Novo produto' : 'Editar produto'} onFechar={onFechar}>
      {/* **O código é a porta** (decisão 238).

          A busca pelo GTIN preenche cinco dos nove campos, e estava
          escondida atrás de um link de 11px no meio da terceira seção —
          o caminho mais rápido da tela era o mais difícil de achar. Agora
          ele é a primeira coisa, num alvo do tamanho da mão; o resto do
          formulário continua inteiro logo abaixo, e quem não tem código
          dispensa a porta e digita.

          Os avisos continuam junto do campo que os produz (decisão 231):
          "UPC e EAN são o mesmo código" subiu com o código de barras. */}
      <div className="grid gap-3 sm:grid-cols-2">
        {!semCodigo && (
          <div className="sm:col-span-2">
            {/* `flat`, e não `frost`: o desfoque é o que custa no
                compositor, e este painel está DENTRO de uma folha opaca —
                não há nada atrás dele para desfocar. Ver o orçamento no
                docblock do `Glass`. */}
            <Glass
              level="flat"
              as="button"
              type="button"
              className="flex w-full flex-col items-center gap-1 px-4 py-5 text-center"
              onClick={() => {
                setEscaneando((atual) => !atual)
              }}
            >
              <span aria-hidden="true" className="text-3xl leading-none">
                {escaneando ? '\u2715' : '\u26f6'}
              </span>
              <span className="text-sm font-medium">
                {escaneando ? 'Fechar a câmera' : 'Escanear o código de barras'}
              </span>
              <span className="text-xs text-[var(--text-muted)]">ou digitar e colar abaixo</span>
            </Glass>

            <button
              type="button"
              className="alvo mt-1 w-full text-xs text-[var(--text-muted)] underline"
              onClick={() => {
                setSemCodigo(true)
              }}
            >
              {/* "Cadastrar sem código" mentia ao editar: quem chegou aqui já
                  cadastrou. "Continuar" serve aos dois modos. */}
              Continuar sem código
            </button>
          </div>
        )}

        {escaneando && (
          <div className="sm:col-span-2">
            <Scanner
              caso="produto"
              onFechar={() => {
                setEscaneando(false)
              }}
              onLer={(texto) => {
                setCodigo(texto)
                setEscaneando(false)
                // Escanear e buscar são o mesmo gesto na cabeça de quem
                // está com a embalagem na mão: ler o código e não buscar
                // seria pedir um toque a mais por nada.
                void buscarPeloCodigo(texto)
              }}
            />
          </div>
        )}

        <label className="text-sm sm:col-span-2">
          <span className="block text-xs text-[var(--text-muted)]">Código de barras</span>
          <input
            inputMode="numeric"
            value={codigo}
            aria-label="Código de barras"
            placeholder="EAN-13 ou UPC"
            className="w-full rounded-lg border border-[var(--border)] bg-transparent px-2 py-1"
            onChange={(e) => {
              mudarCodigo(e.target.value)
            }}
          />
          {/* O aviso aparece enquanto digita, e NÃO impede o cadastro:
              código interno de mercado existe, e o que não pode é ele casar
              com outro produto por acidente. */}
          {codigo.trim() !== '' && gtinOf(codigo) === null && (
            <span className="mt-1 block text-xs text-[var(--text-muted)]">
              Não fecha no dígito verificador — fica guardado, mas não casa com a nota.
            </span>
          )}
        </label>

        {buscando && (
          <p className="text-xs text-[var(--text-muted)] sm:col-span-2" role="status">
            Perguntando à base…
          </p>
        )}

        {/* O resultado da busca, dito por inteiro.
            "encontrado — confira antes de salvar" não dizia encontrado
            O QUÊ, e a pessoa tinha de caçar os campos que mudaram. */}
        {achado !== null && !buscando && (
          <div className="sm:col-span-2" role="status">
            {achado.tipo === 'achou' && (
              <p
                className="rounded-[var(--radius-sm)] border px-3 py-2 text-xs"
                style={{
                  borderColor: 'color-mix(in oklab, var(--economia) 40%, transparent)',
                  background: 'color-mix(in oklab, var(--economia) 9%, transparent)',
                  color: 'var(--economia)',
                }}
              >
                {`\u2713 ${achado.nome ?? 'Encontrado na base'} — `}
                {achado.quantos === 1
                  ? '1 campo preenchido'
                  : `${String(achado.quantos)} campos preenchidos`}
              </p>
            )}
            {achado.tipo === 'nada' && (
              <p className="text-xs text-[var(--text-muted)]">
                Não achei este código na base. O código fica guardado e casa com a nota do mesmo
                jeito — só o resto é que vai à mão.
              </p>
            )}
            {achado.tipo === 'falhou' && (
              <p className="text-xs text-[var(--text-muted)]">
                A busca não completou.{' '}
                <button
                  type="button"
                  className="underline"
                  onClick={() => {
                    void buscarPeloCodigo()
                  }}
                >
                  Tentar de novo
                </button>{' '}
                — ou preencher à mão.
              </p>
            )}
          </div>
        )}

        <Aviso>
          UPC e EAN são o mesmo código — o de 12 dígitos é o de 13 sem o zero da frente, e os dois
          casam com a mesma nota.
        </Aviso>

        <Secao>Identidade</Secao>

        <div className="sm:col-span-2">
          {/* A da base só é anunciada enquanto ninguém escolheu outra: a
              escolhida ganha na hora de salvar, e dizer "da base" ao lado
              de uma foto que já perdeu seria o rótulo mentindo. */}
          {daBase.has('imageUrl') && foto === null && (
            <Rotulo daBase>Foto sugerida pelo código</Rotulo>
          )}
          <EscolherFoto
            foto={foto}
            atual={removeuFoto ? null : (produto?.imageUrl ?? null)}
            onEscolher={(escolhida) => {
              // Tirar quando havia uma salva é uma INTENÇÃO de apagar, e ela só
              // se cumpre no salvar — ver o cabeçalho do `EscolherFoto`.
              if (escolhida === null && produto?.imageUrl != null) setRemoveuFoto(true)
              if (escolhida !== null) setRemoveuFoto(false)
              setFoto(escolhida)
            }}
          />
        </div>

        <label className="text-sm">
          <Rotulo daBase={daBase.has('name')}>Nome</Rotulo>
          <input
            value={nome}
            aria-label="Nome do produto"
            className="w-full rounded-lg border border-[var(--border)] bg-transparent px-2 py-1"
            onChange={(e) => {
              setNome(e.target.value)
              esqueceAOrigem('name')
            }}
          />
        </label>

        <label className="text-sm">
          <Rotulo daBase={daBase.has('brand')}>Marca</Rotulo>
          <input
            value={marca}
            aria-label="Marca"
            className="w-full rounded-lg border border-[var(--border)] bg-transparent px-2 py-1"
            onChange={(e) => {
              setMarca(e.target.value)
              esqueceAOrigem('brand')
            }}
          />
        </label>

        <div className="text-sm sm:col-span-2">
          <span className="block text-xs text-[var(--text-muted)]">Categoria</span>
          <SeletorDeCategoria
            categorias={categorias}
            value={categoria}
            onChange={setCategoria}
            onCriou={onCategoriasMudaram}
          />
        </div>

        <Secao>Tamanho</Secao>

        <fieldset className="text-sm">
          <legend>
            <Rotulo daBase={daBase.has('size')}>Tamanho</Rotulo>
          </legend>
          <div className="flex gap-2">
            <input
              inputMode="decimal"
              value={tamanho}
              aria-label="Tamanho"
              placeholder="500"
              className="w-20 rounded-lg border border-[var(--border)] bg-transparent px-2 py-1"
              onChange={(e) => {
                setTamanho(e.target.value)
                esqueceAOrigem('size')
              }}
            />
            <select
              value={unidade}
              aria-label="Unidade"
              className="rounded-lg border border-[var(--border)] bg-transparent px-2 py-1"
              onChange={(e) => {
                setUnidade(e.target.value as Unit)
              }}
            >
              {UNITS.map((u) => (
                <option key={u} value={u}>
                  {u}
                </option>
              ))}
            </select>
          </div>
        </fieldset>

        <label className="text-sm">
          <span className="block text-xs text-[var(--text-muted)]">Unidades no pacote</span>
          <input
            inputMode="numeric"
            value={pacote}
            aria-label="Unidades no pacote"
            className="w-20 rounded-lg border border-[var(--border)] bg-transparent px-2 py-1"
            onChange={(e) => {
              setPacote(e.target.value)
            }}
          />
        </label>

        <Aviso>
          Sem tamanho, o produto entra no catálogo mas fica fora das comparações — não há unidade
          base para dividir o preço.
        </Aviso>

        {/* A Amazon é um código de OUTRO catálogo, e por isso não subiu com
            o de barras: aquele identifica a embalagem no mundo, este
            identifica a página numa loja. */}
        <Secao>Amazon</Secao>

        <label className="text-sm sm:col-span-2">
          <span className="block text-xs text-[var(--text-muted)]">ASIN</span>
          <input
            value={asin}
            aria-label="ASIN"
            placeholder="B08N5WRWNW ou o link do produto"
            className="w-full rounded-lg border border-[var(--border)] bg-transparent px-2 py-1"
            onChange={(e) => {
              setAsin(e.target.value)
            }}
          />
          {asin.trim() !== '' && asinOf(asin) === null && (
            <span className="mt-1 block text-xs text-[var(--text-muted)]">
              Um ASIN tem dez caracteres. Dá para colar o link do produto.
            </span>
          )}
        </label>
      </div>
      <div className="mt-4 flex items-center gap-2">
        <Botao tom="primario" type="button" disabled={salvando} onClick={salvar}>
          {criando ? 'Salvar produto' : 'Salvar alterações'}
        </Botao>
        <Botao type="button" onClick={onFechar}>
          Cancelar
        </Botao>
      </div>

      {erro !== null && (
        <p role="alert" className="mt-2 text-sm text-[var(--negative)]">
          {erro}
        </p>
      )}
    </FolhaDeBaixo>
  )
}
